<script setup lang="tsx">
import type { PageRequest, PageResponse, Response, Responsible, RoleWithUsers, SaveRolesRequest } from '@/models/api-generated'
import { computed, ref, watch, type WatchStopHandle } from 'vue'
import RoleRow from './RoleRow.vue'
import http from '@/services/http'
import { setNavLock } from '@/services/navlock'
import Modal from '../shared/Modal.vue'
import FilterContainer from '@/components/filter/FilterContainer.vue'
import useRoleColumns from './columns'
import columnStore from '@/stores/column'
import TextFilter from '../filter/TextFilter.vue'
import UserFilter from '../filter/UserFilter.vue'
import { refreshCallback } from '../user/shared'
import ConfirmDeleteDialog from '../user/ConfirmDeleteDialog.vue'
import ReplaceResponsibleDialog from '../user/ReplaceResponsibleDialog.vue'
import { roleRefreshCallback } from './shared'
import type { RoleFilter } from '@/models/filters-generated'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'

const { columns, all: allColumns } = useRoleColumns()
const root = columnStore().defineComponent('roles', fetch)
roleRefreshCallback.value = root.triggerFetch
const toaster = toastStore()
const { t } = useI18n()

const showConfirmDialog = ref(false)
const isSaving = ref(false)
const showDeleteDialog = ref(false)
const showRespDialog = ref(false)
const currentRoleAsResponsible = ref({} as Responsible)
const all = ref<RoleWithUsers[]>([])
const changed = ref(new Set<number>())
const deleted = ref(new Map<number, Responsible | null | undefined>())
const expanded = ref(new Set<number>())
const watches: WatchStopHandle[] = []
const sessionIdsBefore = ref<number[]>([])

async function fetch(signal: AbortSignal) {
	watches.forEach((x) => x())
	watches.splice(0, watches.length)

	const request: PageRequest<RoleFilter> = {
		page: 1,
		limit: 1000000,
		filter: root.filterValue() as RoleFilter,
		orderBy: root.orderValue(),
	}

	const { data } = await http.post<Response<PageResponse<RoleWithUsers>>>('api/role/list', request, { signal })
	if (data.data?.items) all.value = data.data.items
	sessionIdsBefore.value = all.value.filter((x) => x.users.some((u) => u.id === sessionMap.user.id)).map((x) => x.role.id)

	for (let i = 0; i < all.value.length; i++) {
		const a = all.value[i]
		const cb = () => changed.value.add(a.role.id)
		watches.push(
			watch(() => a.role.name, cb),
			watch(() => a.role.description, cb),
			watch(() => a.role.permissions, cb, { deep: true }),
			watch(() => a.users, cb),
			watch(
				() => a.role.isExpanded,
				(isExpanded) => {
					if (isExpanded) expanded.value.add(a.role.id)
					else expanded.value.delete(a.role.id)
				},
			),
		)

		if (expanded.value.has(a.role.id)) all.value[i].role.isExpanded = true
	}
}

function addRole() {
	all.value.push({
		role: {
			id: 0,
			readOnly: false,
			isExpanded: true,
			name: '',
			description: '',
			permissions: [],
		},
		users: [],
	})
}

function deleteRole(def: RoleWithUsers) {
	if (def.role.id === 0) {
		const idx = all.value.indexOf(def)
		if (idx >= 0) all.value.splice(idx, 1)
	} else {
		if (deleted.value.has(def.role.id)) {
			deleted.value.delete(def.role.id)
		} else {
			currentRoleAsResponsible.value = roleAsResponsible(def.role.id)
			showDeleteDialog.value = true
		}
	}
}

function replaceResponsibilities(def: RoleWithUsers) {
	currentRoleAsResponsible.value = roleAsResponsible(def.role.id)
	showRespDialog.value = true
}

function confirmDelete(replacementResponsible?: Responsible) {
	showDeleteDialog.value = false
	deleted.value.set(currentRoleAsResponsible.value.id, replacementResponsible ?? null)
}

async function confirmReplace(replacementResponsible?: Responsible) {
	if (replacementResponsible) {
		await http.post(`api/role/changeResponsibilities/${currentRoleAsResponsible.value.id}`, replacementResponsible)
		showRespDialog.value = false
		toaster.showSuccess(t('misc.successfullyResponsibilityChanged'))
		roleRefreshCallback.value?.()
	}
}

const numChanges = computed(() => changed.value.size + deleted.value.size + all.value.filter((x) => x.role.id === 0).length)

const canSave = computed(() => {
	if (!numChanges.value) return false

	for (const def of all.value) {
		if (!def.role.name) return false
	}

	return true
})

watch(canSave, (can) => setNavLock('roles', can))

async function save() {
	const after = all.value.filter((x) => x.users.some((u) => u.id === sessionMap.user.id)).map((x) => x.role.id)
	const rolesChanged = sessionIdsBefore.value.length !== after.length || sessionIdsBefore.value.some((id, idx) => id !== after[idx])

	isSaving.value = true
	const request: SaveRolesRequest = {
		added: all.value.filter((x) => x.role.id === 0),
		changed: all.value.filter((x) => changed.value.has(x.role.id) && !deleted.value.has(x.role.id)),
		deleted: Object.fromEntries(
			Array.from(deleted.value.entries())
				.filter(([_, val]) => val !== undefined)
				.map(([key, val]) => [key, val!]),
		),
	}

	await http.post('api/role/save', request)
	await reset()
	if (rolesChanged) {
		window.location.reload()
	}
	if (refreshCallback.value) refreshCallback.value()
	isSaving.value = false
}

async function reset() {
	showConfirmDialog.value = false

	all.value = []
	changed.value.clear()
	deleted.value.clear()
	root.triggerFetch()
}

async function startReset() {
	if (canSave.value) showConfirmDialog.value = true
	else await reset()
}

function roleAsResponsible(id: number) {
	const role = all.value.find((x) => x.role.id === id) as RoleWithUsers | undefined
	const responsible: Responsible = {
		id: role?.role.id ?? 0,
		type: 'roles',
		trackBy: `roles_${role?.role.id ?? 0}`,
		label: role?.role.name ?? '',
	}
	return responsible
}

defineExpose({
	save,
	startReset,
	addRole,
	numChanges,
	canSave,
	isSaving,
})
</script>

<template>
	<div class="row" :class="canSave ? 'disabled' : ''">
		<FilterContainer :columns="allColumns" :hideButton="true" bookmark="roles-bookmark">
			<div class="p-2 filter-col" v-if="columns.name.value.isVisible">
				<TextFilter id="name-filter-role" v-model="columns.name.value"></TextFilter>
			</div>

			<div class="p-2 filter-col" v-if="columns.description.value.isVisible">
				<TextFilter id="description-filter-role" v-model="columns.description.value"></TextFilter>
			</div>

			<div class="p-2 filter-col" v-if="columns.user.value.isVisible">
				<UserFilter id="description-filter-user" v-model="columns.user.value"></UserFilter>
			</div>
		</FilterContainer>
	</div>

	<div class="container-fluid rounded-container shadow-sm p-3 px-4">
		<div class="row">
			<div class="col-4">
				<h6 class="margin">{{ $t('role') }}</h6>
			</div>
			<div class="col-6">
				<h6>{{ $t('description') }}</h6>
			</div>
			<div class="col-1">
				<h6>{{ $t('user.label', 2) }}</h6>
			</div>
		</div>

		<template v-if="all?.length">
			<RoleRow
				v-for="(rwu, i) in all"
				:key="rwu.role.id"
				v-model="all[i]"
				:is-changed="changed.has(rwu.role.id)"
				:is-deleted="deleted.has(rwu.role.id)"
				:is-odd="i % 2 === 0"
				@delete="deleteRole(all[i])"
				@replaceResponsibilities="replaceResponsibilities(all[i])"
			></RoleRow>
		</template>
	</div>

	<ConfirmDeleteDialog v-model="showDeleteDialog" :responsible="currentRoleAsResponsible" @confirm="confirmDelete"></ConfirmDeleteDialog>

	<ReplaceResponsibleDialog
		v-model="showRespDialog"
		:responsible="currentRoleAsResponsible"
		@confirm="confirmReplace"
	></ReplaceResponsibleDialog>

	<Modal v-model="showConfirmDialog" @confirm="reset">
		<template #header>
			<h5>{{ $t('misc.confirm') }}</h5>
		</template>
		<template #body>
			<p>{{ $t('misc.loseUnsavedChanges') }}</p>
		</template>
	</Modal>
</template>

<style lang="sass" scoped>
.margin
  margin-left: 38px

.disabled
  opacity: .6
  pointer-events: none

h6
  color: var(--bs-body-color)
</style>
